/**
 * Genetic Algorithm for Mesh Placement Optimization
 *
 * This algorithm uses evolutionary principles to find optimal mesh unit placement.
 * It simulates thousands of possible configurations and selects the best performers.
 * It iteratively determines the number of units needed for Basic, Medium, and High coverage targets,
 * keeping the primary router location fixed and using physics-based simulations.
 */
import { WIRELESS_SPECS } from '../constants';
// Import necessary physics/simulation helpers from logic.js
import {
    getMaterialPenetrationLoss,
    createCoverageGrid,
    simulateCoverageGridForFrequency,
    mapFrequenciesToSpeedTiers
} from '../logic';

// Constants for genetic algorithm
const POPULATION_SIZE = 50;
const GENERATIONS = 20; // Generations per unit count optimization
const MUTATION_RATE = 0.1;
const CROSSOVER_RATE = 0.7;
const ELITISM_COUNT = 3;
const MAX_UNITS_CAP = 7; // Cap units for GA due to performance cost
const TARGET_COVERAGE_PERCENT = 99; // Target coverage percentage
// Define signal strength thresholds (dBm) for coverage levels
const HIGH_SENSITIVITY_THRESHOLD = -75;
const MEDIUM_SENSITIVITY_THRESHOLD = -80;
const BASIC_SENSITIVITY_THRESHOLD = -85;
const GRID_STEP = 10; // Use consistent grid step from logic.js

// --- Helper Functions ---

const getPrimaryRouterPlacementDescription = (formData) => {
  if (formData.routerLocation === 'livingRoom') return 'Place your primary router in the living room, preferably in a central location and elevated.';
  if (formData.routerLocation === 'office') return 'Place your primary router in the home office, preferably elevated and away from other electronics.';
  if (formData.routerLocation === 'bedroom') return 'Place your primary router in the bedroom, preferably elevated and away from the bed.';
  if (formData.routerLocation === 'kitchen') return 'Place your primary router in the kitchen, but keep it away from appliances like microwaves that can cause interference.';
  if (formData.routerLocation === 'hallway') return 'Place your primary router in the hallway, which provides good central coverage for the surrounding rooms.';
  if (formData.routerLocation === 'basement') return 'Place your primary router in the basement, but be aware that this may limit coverage on upper floors.';
  if (formData.routerLocation === 'custom' && formData.customRouterLocation) return `Place your primary router in ${formData.customRouterLocation}, preferably elevated for better signal propagation.`;
  return 'Place your primary router in a central location in your home, preferably elevated for better signal propagation.';
};

// **Modified**: Helper to determine primary router coordinates using floorPlanLayout dimensions
const getPrimaryRouterLocation = (formData, floorPlanLayout) => {
  // Start at center in meters using layout dimensions
  let xMeters = floorPlanLayout.layoutWidthMeters / 2;
  let yMeters = floorPlanLayout.layoutHeightMeters / 2;

  // Adjust based on location preference (relative adjustments)
  if (formData.routerLocation === 'livingRoom') { xMeters *= 1.2; yMeters *= 0.8; }
  else if (formData.routerLocation === 'office') { xMeters *= 0.6; yMeters *= 0.6; }
  else if (formData.routerLocation === 'bedroom') { xMeters *= 1.4; yMeters *= 0.6; }
  else if (formData.routerLocation === 'kitchen') { xMeters *= 0.6; yMeters *= 1.4; }
  // Add other locations if needed

  // Clamp to bounds (in meters) using layout dimensions
  return {
      x: Math.max(0, Math.min(floorPlanLayout.layoutWidthMeters, xMeters)),
      y: Math.max(0, Math.min(floorPlanLayout.layoutHeightMeters, yMeters))
  };
};

// Removed getFloorPlanDimensions

// **Removed**: createHomeModel is no longer needed as floorPlanLayout provides dimensions and obstacles

// **Modified**: Generate random positions for MESH nodes only, using floorPlanLayout dimensions
const generateRandomMeshPositions = (meshUnitCount, floorPlanLayout, existingPositions = []) => {
    const positions = [];
    const minDistanceMeters = 3; // Min distance between units in meters
    const maxAttempts = 50;
    const allUnits = [...existingPositions]; // Include fixed primary (already in meters)
    const widthMeters = floorPlanLayout.layoutWidthMeters;
    const heightMeters = floorPlanLayout.layoutHeightMeters;

    for (let i = 0; i < meshUnitCount; i++) {
        let position; let attempts = 0; let valid = false;
        while (!valid && attempts < maxAttempts) {
            // Generate random position within layout meter bounds
            position = { x: Math.random() * widthMeters, y: Math.random() * heightMeters };
            valid = true;
            for (const existingPos of allUnits) { // Check against all existing units (meters)
                const distance = Math.sqrt(Math.pow(position.x - existingPos.x, 2) + Math.pow(position.y - existingPos.y, 2));
                if (distance < minDistanceMeters) { valid = false; break; }
            }
            attempts++;
        }
        // Fallback if no valid position found after attempts
        if (!valid) { position = { x: Math.random() * widthMeters, y: Math.random() * heightMeters }; }
        positions.push(position); // Position is in meters
        allUnits.push(position); // Add newly generated meter position for next check
    }
    return positions; // Returns array of meter coordinates
};

// **Modified**: Initialize population using floorPlanLayout dimensions
const initializePopulation = (size, unitCount, floorPlanLayout, fixedPrimaryPos) => {
  const pop = [];
  const meshUnitCount = unitCount - 1;
  for (let i = 0; i < size; i++) {
      // Generate random mesh positions using layout dimensions
      const meshPositions = generateRandomMeshPositions(meshUnitCount, floorPlanLayout, [fixedPrimaryPos]);
      // Combine fixed primary (meters) with random mesh positions (meters)
      pop.push({ positions: [fixedPrimaryPos, ...meshPositions] });
  }
  return pop;
};

// getRouterRange remains less critical
const getRouterRange = (specs) => { return specs?.RANGE || 15; };

// --- Fitness Calculation (Uses Meter-Based Simulation and floorPlanLayout) ---
const calculateFitness = (positions, floorPlanLayout, specs, formData) => {
    // positions are already in meters
    // floorPlanLayout contains meter dimensions and meter-based obstacles

    // Create grids using area (logic.js handles internal meter conversion)
    // TODO: Consider using floorPlanLayout dimensions directly if createCoverageGrid supports it
    const aspectRatio = floorPlanLayout.layoutWidthMeters / floorPlanLayout.layoutHeightMeters || 1;
    const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
    const grid5 = createCoverageGrid(formData.area, aspectRatio);
    const grid6 = (specs.BANDS?.includes('6GHz')) ? createCoverageGrid(formData.area, aspectRatio) : null;

    // Define frequency parameters
    const params2_4 = { frequencyGHz: 2.4, transmitPowerDBm: specs.TX_POWER_2_4GHZ || 20, penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType) };
    const params5 = { frequencyGHz: 5, transmitPowerDBm: specs.TX_POWER_5GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(5, formData.wallDensity, formData.constructionType) };
    const params6 = grid6 ? { frequencyGHz: 6, transmitPowerDBm: specs.TX_POWER_6GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(6, formData.wallDensity, formData.constructionType) } : null;

    // Simulate coverage using meter-based positions and obstacles from floorPlanLayout
    simulateCoverageGridForFrequency(positions, grid2_4, floorPlanLayout.obstacles, params2_4);
    simulateCoverageGridForFrequency(positions, grid5, floorPlanLayout.obstacles, params5);
    if (grid6 && params6) {
        simulateCoverageGridForFrequency(positions, grid6, floorPlanLayout.obstacles, params6);
    }

    // Map frequencies using the updated grids
    const percentages = mapFrequenciesToSpeedTiers(grid2_4, grid5, grid6, specs);

    // Fitness score prioritizes meeting basic/medium coverage, then high, then spread
    const basicScore = Math.min(1, percentages.basic / TARGET_COVERAGE_PERCENT);
    const mediumScore = Math.min(1, percentages.medium / TARGET_COVERAGE_PERCENT);
    const highScore = Math.min(1, percentages.high / TARGET_COVERAGE_PERCENT);

    // Distance Score (Spread) - applied only to mesh nodes (using meter coords)
    let totalMinDistance = 0;
    const meshPositions = positions.slice(1); // Exclude primary
    if (meshPositions.length > 0) {
        for (let i = 0; i < positions.length; i++) { // Check distance from all nodes (incl primary)
            let minDistance = Infinity;
            for (let j = 0; j < positions.length; j++) {
                if (i === j) continue;
                const dist = Math.sqrt(Math.pow(positions[i].x - positions[j].x, 2) + Math.pow(positions[i].y - positions[j].y, 2)); // meters
                minDistance = Math.min(minDistance, dist);
            }
            if (i > 0) { totalMinDistance += minDistance; } // Only add score for mesh nodes
        }
    }
    // Use layout width (meters) for normalization
    const layoutWidthMeters = floorPlanLayout.layoutWidthMeters;
    const avgMinDistance = meshPositions.length > 0 ? totalMinDistance / meshPositions.length : layoutWidthMeters;
    const distanceScore = Math.min(1, avgMinDistance / (layoutWidthMeters * 0.20)); // Normalize spread score based on layout width

    // Weighted fitness score
    return (basicScore * 0.4) + (mediumScore * 0.3) + (highScore * 0.2) + (distanceScore * 0.1);
};

// **Modified**: evaluatePopulation passes floorPlanLayout
const evaluatePopulation = (pop, floorPlanLayout, specs, formData) => {
    return pop.map(ind => ({ ...ind, fitness: calculateFitness(ind.positions, floorPlanLayout, specs, formData) }));
};

// selectParents remains the same conceptually
const selectParents = (pop) => { const parents = []; const ts = 3; for (let i = 0; i < pop.length; i++) { let best = null; for (let j = 0; j < ts; j++) { const randInd = pop[Math.floor(Math.random() * pop.length)]; if (best === null || randInd.fitness > best.fitness) { best = randInd; } } parents.push(best); } return parents; };

// Crossover remains the same conceptually (operates on array indices)
const crossover = (p1, p2) => {
    const fixedPrimary = p1.positions[0]; // Primary is always at index 0 (meters)
    const meshP1 = p1.positions.slice(1); // Meter coords
    const meshP2 = p2.positions.slice(1); // Meter coords
    if (meshP1.length === 0) return [{ positions: [fixedPrimary] }, { positions: [fixedPrimary] }];

    const cp = meshP1.length > 0 ? Math.floor(Math.random() * meshP1.length) : 0;
    const child1Mesh = [...meshP1.slice(0, cp), ...meshP2.slice(cp)];
    const child2Mesh = [...meshP2.slice(0, cp), ...meshP1.slice(cp)];
    return [{ positions: [fixedPrimary, ...child1Mesh] }, { positions: [fixedPrimary, ...child2Mesh] }]; // Children have meter coords
};

// createOffspring remains the same conceptually
const createOffspring = (parents, rate) => { const off = []; const shuf = [...parents].sort(() => 0.5 - Math.random()); for (let i = 0; i < shuf.length - 1; i += 2) { if (Math.random() < rate) { off.push(...crossover(shuf[i], shuf[i + 1])); } else { off.push({ positions: [...shuf[i].positions] }, { positions: [...shuf[i + 1].positions] }); } } if (off.length < parents.length) { off.push({ positions: [...shuf[shuf.length - 1].positions] }); } return off.slice(0, parents.length); };

// **Modified**: Mutate only mesh node positions (using floorPlanLayout dimensions)
const mutatePosition = (pos, floorPlanLayout) => {
    const widthMeters = floorPlanLayout.layoutWidthMeters;
    const heightMeters = floorPlanLayout.layoutHeightMeters;
    const mutationStrength = Math.max(widthMeters, heightMeters) * 0.05; // Mutate by up to 5% of max dimension
    const offsetX = (Math.random() - 0.5) * mutationStrength * Math.random();
    const offsetY = (Math.random() - 0.5) * mutationStrength * Math.random();
    const newX = Math.max(0, Math.min(widthMeters, pos.x + offsetX)); // Clamp to layout meter bounds
    const newY = Math.max(0, Math.min(heightMeters, pos.y + offsetY)); // Clamp to layout meter bounds
    return { ...pos, x: newX, y: newY };
};
// **Modified**: mutateOffspring passes floorPlanLayout
const mutateOffspring = (off, floorPlanLayout, rate) => {
    return off.map(ind => {
        const mutatedPositions = ind.positions.map((pos, index) => {
            // Only mutate mesh nodes (index > 0)
            if (index > 0 && Math.random() < rate) {
                return mutatePosition(pos, floorPlanLayout); // Pass layout bounds
            }
            return pos; // Keep primary (index 0, meters) and non-mutated nodes the same
        });
        return { ...ind, positions: mutatedPositions }; // Returns meter coords
    });
};

// applyElitism remains the same conceptually
const applyElitism = (curr, off, count) => { const sCurr = [...curr].sort((a, b) => b.fitness - a.fitness); const elites = sCurr.slice(0, count); const sOff = [...off].sort((a, b) => b.fitness - a.fitness); return [...elites, ...sOff.slice(0, curr.length - count)]; };
// getBestSolution remains the same conceptually
const getBestSolution = (pop) => { return pop.reduce((b, c) => (c.fitness > b.fitness ? c : b), pop[0]); };

// **Modified**: formatSolutionPlacements uses meter coords
const formatSolutionPlacements = (positions, formData) => {
  if (!Array.isArray(positions)) {
      console.error("formatSolutionPlacements received non-array:", positions);
      return [];
  }
  // Positions are already in meters
  return positions.map((pos, index) => ({
    type: index === 0 ? 'primary' : 'mesh',
    position: { x: pos.x, y: pos.y }, // Store meter coordinates
    description: index === 0 ? getPrimaryRouterPlacementDescription(formData) : `Place mesh unit ${index + 1} (Genetic Algo).`,
    tips: []
  }));
};

// --- Exported Functions ---

/**
 * Runs the Genetic Algorithm to find the best placement for a GIVEN number of units,
 * keeping the primary router fixed. Uses floorPlanLayout.
 */
export const calculateGeneticPlacementsForCount = (formData, routerStandard, routerSpecs, unitCount, floorPlanLayout) => {
  if (unitCount < 1 || !floorPlanLayout) return []; // Check for layout

  // Use dimensions and obstacles from floorPlanLayout
  const widthMeters = floorPlanLayout.layoutWidthMeters;
  const heightMeters = floorPlanLayout.layoutHeightMeters;
  // const obstacles = floorPlanLayout.obstacles; // Obstacles are used in fitness calculation

  // Determine primary router location using layout dimensions
  const primaryPos = getPrimaryRouterLocation(formData, floorPlanLayout); // Meter coords

  // Initialize population using layout dimensions
  let population = initializePopulation(POPULATION_SIZE, unitCount, floorPlanLayout, primaryPos);
  // Evaluate population using layout
  population = evaluatePopulation(population, floorPlanLayout, routerSpecs, formData);

  for (let gen = 0; gen < GENERATIONS; gen++) {
    const parents = selectParents(population);
    const offspring = createOffspring(parents, CROSSOVER_RATE); // Contains meter coords
    // Mutate offspring using layout bounds
    const mutated = mutateOffspring(offspring, floorPlanLayout, MUTATION_RATE); // Contains meter coords
    // Evaluate mutated offspring using layout
    const evaluated = evaluatePopulation(mutated, floorPlanLayout, routerSpecs, formData);
    population = applyElitism(population, evaluated, ELITISM_COUNT);
  }
  const best = getBestSolution(population); // Best solution has meter coords
  // Format the best solution (meter coords)
  return formatSolutionPlacements(best?.positions ?? [primaryPos], formData); // Handle case where GA might fail
};

/**
 * Calculates coverage percentages based on the GA's placements using physics simulation
 * and the provided floorPlanLayout.
 */
export const calculateGeneticCoveragePercentage = (unitCount, placements, formData, routerSpecs, floorPlanLayout) => {
    if (!floorPlanLayout || !placements || placements.length === 0) {
        return { basic: 0, medium: 0, high: 0 };
    }
    // Use obstacles from floorPlanLayout
    const obstacles = floorPlanLayout.obstacles; // Meter coords

    // Extract positions (already in meters from placements)
    const positions = placements.map(p => ({...p.position, type: p.type})); // Meter coords

    // Create grids using area (logic.js handles internal meter conversion)
    // TODO: Consider using floorPlanLayout dimensions directly if createCoverageGrid supports it
    const aspectRatio = floorPlanLayout.layoutWidthMeters / floorPlanLayout.layoutHeightMeters || 1;
    const grid2_4 = createCoverageGrid(formData.area, aspectRatio);
    const grid5 = createCoverageGrid(formData.area, aspectRatio);
    const grid6 = (routerSpecs.BANDS?.includes('6GHz')) ? createCoverageGrid(formData.area, aspectRatio) : null;

    // Define frequency parameters
    const params2_4 = { frequencyGHz: 2.4, transmitPowerDBm: routerSpecs.TX_POWER_2_4GHZ || 20, penetrationLossDB: getMaterialPenetrationLoss(2.4, formData.wallDensity, formData.constructionType) };
    const params5 = { frequencyGHz: 5, transmitPowerDBm: routerSpecs.TX_POWER_5GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(5, formData.wallDensity, formData.constructionType) };
    const params6 = grid6 ? { frequencyGHz: 6, transmitPowerDBm: routerSpecs.TX_POWER_6GHZ || 23, penetrationLossDB: getMaterialPenetrationLoss(6, formData.wallDensity, formData.constructionType) } : null;

    // Simulate coverage using meter-based positions and obstacles from floorPlanLayout
    simulateCoverageGridForFrequency(positions, grid2_4, obstacles, params2_4);
    simulateCoverageGridForFrequency(positions, grid5, obstacles, params5);
    if (grid6 && params6) {
        simulateCoverageGridForFrequency(positions, grid6, obstacles, params6);
    }

    // Map frequencies using the updated grids
    return mapFrequenciesToSpeedTiers(grid2_4, grid5, grid6, routerSpecs);
};


/**
 * Main function for the Genetic Algorithm.
 * Iteratively determines the number of units required to meet coverage targets.
 * Uses the provided floorPlanLayout.
 */
const geneticAlgorithm = (formData, routerStandard, routerSpecs, floorPlanLayout) => {
  // Check if floorPlanLayout is provided
  if (!floorPlanLayout) {
      console.error("Genetic Algorithm requires floorPlanLayout.");
      // Return a default error structure or throw an error
      return {
          coverageLevels: { basic: { units: 0, placements: [] }, medium: { units: 0, placements: [] }, high: { units: 0, placements: [] } },
          coveragePercentages: { basic: 0, medium: 0, high: 0 }
      };
  }

  let currentUnitCount = 0;
  let placements = []; // Will store meter-based placements
  let currentPercentages = { basic: 0, medium: 0, high: 0 };

  let basicResult = null;
  let mediumResult = null;
  let highResult = null;

  console.log(`GeneticAlgorithm: Starting iterative calculation (using meters)...`);

  while (currentUnitCount < MAX_UNITS_CAP) {
    currentUnitCount++;
    console.log(`GeneticAlgorithm: Running GA for ${currentUnitCount} units (Gen: ${GENERATIONS}, Pop: ${POPULATION_SIZE})...`);

    // Run GA to find the best placements using the floorPlanLayout
    placements = calculateGeneticPlacementsForCount(formData, routerStandard, routerSpecs, currentUnitCount, floorPlanLayout);

    // Calculate coverage percentages using the floorPlanLayout
    currentPercentages = calculateGeneticCoveragePercentage(currentUnitCount, placements, formData, routerSpecs, floorPlanLayout);
    console.log(`GeneticAlgorithm: ${currentUnitCount} units -> B:${currentPercentages.basic}%, M:${currentPercentages.medium}%, H:${currentPercentages.high}%`);

    // Check if targets met and store the first time they are met
    if (!basicResult && currentPercentages.basic >= TARGET_COVERAGE_PERCENT) {
      console.log(`GeneticAlgorithm: Basic target met at ${currentUnitCount} units.`);
      basicResult = { units: currentUnitCount, placements: [...placements], coverage: {...currentPercentages} }; // Store meter placements
    }
    if (!mediumResult && currentPercentages.medium >= TARGET_COVERAGE_PERCENT) {
      console.log(`GeneticAlgorithm: Medium target met at ${currentUnitCount} units.`);
      mediumResult = { units: currentUnitCount, placements: [...placements], coverage: {...currentPercentages} }; // Store meter placements
    }
    if (!highResult && currentPercentages.high >= TARGET_COVERAGE_PERCENT) {
      console.log(`GeneticAlgorithm: High target met at ${currentUnitCount} units.`);
      highResult = { units: currentUnitCount, placements: [...placements], coverage: {...currentPercentages} }; // Store meter placements
    }

    // Exit loop if all targets are met
    if (basicResult && mediumResult && highResult) {
       console.log(`GeneticAlgorithm: All targets met.`);
       break;
    }
  }

   // If targets weren't met after reaching the cap, use the results from the max unit count achieved
   const finalResult = { units: currentUnitCount, placements: [...placements], coverage: {...currentPercentages} }; // Use final meter placements
   if (!basicResult) { console.log(`GeneticAlgorithm: Basic target not met, using max units (${currentUnitCount}).`); basicResult = finalResult; }
   if (!mediumResult) { console.log(`GeneticAlgorithm: Medium target not met, using max units (${currentUnitCount}).`); mediumResult = finalResult; }
   if (!highResult) { console.log(`GeneticAlgorithm: High target not met, using max units (${currentUnitCount}).`); highResult = finalResult; }

  // Return results based on when each target was first met, or the final state if not met
  return {
    coverageLevels: {
      basic: { units: basicResult.units, placements: basicResult.placements }, // Meter placements
      medium: { units: mediumResult.units, placements: mediumResult.placements }, // Meter placements
      high: { units: highResult.units, placements: highResult.placements } // Meter placements
    },
    coveragePercentages: { // Report the coverage achieved *at the point the target was met* or the final coverage if not met
        basic: basicResult.coverage.basic,
        medium: mediumResult.coverage.medium,
        high: highResult.coverage.high
    }
  };
};

export default geneticAlgorithm;